import { requirePermission } from "@/lib/casl/route-guards";
import { DataTable } from "@/components/data-table/data-table";
import { columns } from "@/components/mentor-scoring/columns";
import { ChartContainer, ChartTooltip } from "@/components/ui/chart";
import { ScoresDetails, useGetMentorshipScores, useGetMentorshipSnapshots } from "@/lib/api/mentor-scoring";
import { useDataTable } from "@/lib/hooks/use-data-table";
import { createFileRoute, useRouter, useSearch } from "@tanstack/react-router";
import { format } from "date-fns";
import { useState } from "react";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";

export const Route = createFileRoute("/_authenticated/mentor-scoring/")({
  component: MentorScoring,
  beforeLoad: ({ context }) => {
    requirePermission(context.user, "read", "mentor-scoring");
  },
});

function MentorScoring() {
  const [clickedRows, setClickedRows] = useState<Set<string>>(new Set());
  const router = useRouter();
  const { page, per_page, search, customFilter, group, status, year, month } = useSearch({
    strict: false,
  }) as Record<string, string>;

  const currentPage = parseInt(page || "1", 10);
  const pageSize = parseInt(per_page || "20", 10);

  const { data, isPending } = useGetMentorshipScores(
    currentPage,
    pageSize,
    search,
    customFilter,
    group,
    status,
    year,
    month,
    true,
  );

  // grabbing ids for next layer of processing (second call for snapshots)
  const mentorIds = data?.scores.map(score => score.mentor_id) || [];
  const mentorSnapshots = useGetMentorshipSnapshots(
      currentPage,
      pageSize,
      mentorIds,
      search,
      customFilter,
      group,
      status,
      year,
      month,
      false,
  ).data?.scores;

  // second call for average scores
  const { data: allData } = useGetMentorshipScores(
    currentPage,
    5000, // arbitrary large number to grab everything
    search,
    customFilter,
    group,
    status,
    year,
    month,
    true,
  );
  const allMentorIds = allData?.scores.map(score => score.mentor_id) || [];
  const allSnapshots = useGetMentorshipSnapshots(
      currentPage,
      5000, // arbitrary large number to grab everything
      allMentorIds,
      search,
      customFilter,
      group,
      status,
      year,
      month,
      false,
  ).data?.scores;


const generateDistinctColors = (count: number): string[] => {
  if (count <= 0) {
    return [];
  }
  const colors: string[] = [];
  const hueStep = 360 / count;
  
  for (let i = 0; i < count; i++) {
    const hue = Math.floor(i * hueStep);
    colors.push(`hsl(${hue}, 70%, 50%)`);
  }
  
  return colors;
};

const mentorColors = generateDistinctColors(mentorIds.length);
interface ChartDataPoint {
  date: string;
  [mentorKey: `mentor_${string}`]: number;
}

function formatSnapshotsForChart(snapshots: ScoresDetails[]): ChartDataPoint[] {
  const allDates = [...new Set(snapshots.map(s => s.snapshot_date.split('T')[0]))]
    .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

  const allMentorIds = [...new Set(snapshots.map(s => s.mentor_id))];

  return allDates.map(date => {
    const dataPoint: ChartDataPoint = { date };
    
    allMentorIds.forEach(mentorId => {
      const snapshot = snapshots.find(s => 
        s.snapshot_date.startsWith(date) && s.mentor_id === mentorId
      );
      dataPoint[`mentor_${mentorId}`] = snapshot ? parseFloat(snapshot.final_score) : 0;
    });

    return dataPoint;
  });
}

function formatSnapshotsAverage(snapshots: ScoresDetails[]): ChartDataPoint[] {
  const allDates = [...new Set(snapshots.map(s => s.snapshot_date.split('T')[0]))]
    .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

  return allDates.map(date => {
    const snapshotsForDate = snapshots.filter(s => 
      s.snapshot_date.startsWith(date)
    );
    
    const nonZeroScores = snapshotsForDate
      .map(snapshot => parseFloat(snapshot.final_score))
      .filter(score => score !== 0);
    
    const average = nonZeroScores.length > 0 
      ? nonZeroScores.reduce((sum, score) => sum + score, 0) / nonZeroScores.length
      : 0;

    return {
      date,
      average: parseFloat(average.toFixed(2))
    };
  });
}

  const testdata: any = mentorSnapshots ? formatSnapshotsForChart(mentorSnapshots) : [];

  const { table } = useDataTable({
    columns,
    data: data?.scores || [],
    defaultPerPage: pageSize,
    pageCount: -1,
    filterFields: [],
  });

  const handlePageChange = (newPageIndex: number) => {
    const newPage = newPageIndex + 1;
    router.navigate({
      to: router.state.location.pathname,
      search: (prev: any) => ({
        ...prev,
        page: newPage,
      }),
    });
  };

  const handlePageSizeChange = (newPageSize: number) => {
    router.navigate({
      to: router.state.location.pathname,
      search: (prev: any) => ({
        ...prev,
        page: 1,
        per_page: newPageSize,
      }),
    });
  };

  const filterDataForSelectedMentors = (testdata: ChartDataPoint[], selectedMentorIds: Set<string>): ChartDataPoint[] => {
  // averages that are always inserted
  const averageData: any = allSnapshots ? formatSnapshotsAverage(allSnapshots) : [];
  const averageMap = new Map(averageData.map((item: any) => [item.date, item.average]));

  if (!testdata) return [];
  
  return testdata.map(dataPoint => {
    const filteredPoint: ChartDataPoint = { date: dataPoint.date };

    const averageForDate = averageMap.get(dataPoint.date);
    if (averageForDate !== undefined) {
      (filteredPoint as any).mentor_average = averageForDate;
    }

    Object.keys(dataPoint).forEach(key => {
      if (key === 'date') return; 
      if (selectedMentorIds.has(key)) {
        (filteredPoint as any)[key as keyof ChartDataPoint] = dataPoint[key as keyof ChartDataPoint];
      }
    });
    
    return filteredPoint;
  });
};



  const getMentorName = (mentorId: string) => {
    if (!mentorSnapshots) return `Mentor ${mentorId.slice(-4)}`;
    
    const mentorSnapshot = mentorSnapshots.find(s => s.mentor_id === mentorId);
    if (!mentorSnapshot) return `Mentor ${mentorId.slice(-4)}`;
    
    return `${mentorSnapshot.mentor.first_name} ${mentorSnapshot.mentor.last_name}`;
  };

  const completedata = testdata ? filterDataForSelectedMentors(testdata, clickedRows) : [];
  return (
    <div className="m-6 pb-16">
    <ChartContainer 
      config={Object.fromEntries(
        mentorIds.map((id, index) => [
          `mentor_${id}`,
          { label: `Mentor ${id.slice(-4)}`, color: mentorColors[index] }
        ])
      )} 
      className="h-[400px] w-full mb-10"
    >
      <LineChart
        data={completedata}
        margin={{ left: 12, right: 12 }}
      >
        <CartesianGrid vertical={false} />
        <XAxis
          dataKey="date"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          tickFormatter={(value) => format(new Date(value), 'MMM d')}
        />
        <YAxis
          tickLine={false}
          axisLine={false}
          tickMargin={8}
        />
        <ChartTooltip
          content={({ active, payload }) => {
            if (!active || !payload || payload.length === 0) return null;
            
            return (
              <div className="bg-background p-4 border rounded-lg shadow-lg z-100">
                <p className="font-bold mb-2">
                  {format(new Date(payload[0].payload.date), 'MMM d, yyyy')}
                </p>
                <div className="space-y-1">
                  {payload
                    .slice()
                    .reverse()
                    .map((entry) => (
                      <div key={entry.name} className="flex items-center gap-2">
                        <div 
                          className="h-3 w-3 rounded-full flex-shrink-0" 
                          style={{ backgroundColor: entry.color }} 
                        />
                        <span className="text-sm font-medium">
                          {String(entry.name).replace('mentor_', 'Mentor ')}: 
                          <span className="ml-1 font-bold">
                            {Number(entry.value).toFixed(1)}
                          </span>
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            );
          }}
        />
        {mentorIds.map((mentorId, index) => (
          <Line
            key={mentorId}
            dataKey={`mentor_${mentorId}`}
            name={getMentorName(mentorId)}
            type="monotone"
            stroke={mentorColors[index]}
            strokeWidth={1.5}
            dot={false}
            activeDot={{
              r: 5,
              strokeWidth: 2,
              stroke: mentorColors[index],
              fill: '#fff',
            }}
            isAnimationActive={true}
          />
        ))}
        <Line
          key="average"
          dataKey="mentor_average"
          name="Average"
          type="monotone"
          stroke="#000000"
          strokeWidth={2}
          strokeDasharray="5 5"
          dot={false}
          activeDot={{
            r: 5,
            strokeWidth: 2,
            stroke: "#000000",
            fill: '#fff',
          }}
          isAnimationActive={true}
        />
      </LineChart>
    </ChartContainer>
      <DataTable
        getRowClassName={(row) => {
          const isClicked = clickedRows.has(`mentor_${row.mentor_id}`);
          return isClicked ? "bg-blue-100 hover:bg-blue-200" : "";
        }}
        table={table}
        getCanNextPage={() => {
          return !data?.isLastPage;
        }}
        getCanPreviousPage={() => {
          return currentPage > 1;
        }}
        onRowClick={(row) => {
          setClickedRows(prev => {
            const newSet = new Set(prev);
            if (newSet.has(`mentor_${row.mentor_id}`)) {
              newSet.delete(`mentor_${row.mentor_id}`);
            } else {
              newSet.add(`mentor_${row.mentor_id}`);
            }
            return newSet;
          });
        }}
        isPending={isPending}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      >
      </DataTable>
    </div>
  );
}


